import React, { useEffect, useRef } from "react";

// Minimal type for items in the question bank. The real repo uses `photo` on each question
// but we'll be tolerant and look for common keys.
type QuestionLike = {
	photo?: string;
	image?: string;
	img?: string;
	[key: string]: any;
};

interface PhotoLoaderProps {
	questionBank: QuestionLike[];
	// optional callback invoked when all images are loaded (or attempted)
	onLoaded?: (result: { loaded: number; attempted: number }) => void;
}

/**
 * PhotoLoader preloads all images referenced in a question bank so that
 * when the app later renders them the browser cache makes them appear quickly.
 *
 * Implementation notes:
 * - Scans each item for `photo`, `image`, or `img` fields containing a string URL.
 * - Uses browser Image objects to trigger network loading.
 * - Keeps a small in-memory ref of Image objects to avoid early GC while loading.
 * - Silently swallows load errors but reports counts through onLoaded.
 */
export default function PhotoLoader(props: PhotoLoaderProps) {
	const { questionBank, onLoaded } = props;
	const imagesRef = useRef<HTMLImageElement[]>([]);

  useEffect(() => {
		if (!questionBank || questionBank.length === 0) return;

		// extract URLs from the question bank items
		const urls: string[] = [];
		for (const item of questionBank) {
			const possible = (item && (item.photo || item.image || item.img)) as any;
			if (typeof possible === "string" && possible.trim() !== "") {
				urls.push(possible);
			}
		}

		if (urls.length === 0) {
			return;
		}

		imagesRef.current = [];
		let attempts = 0;
		let success = 0;

		urls.forEach((url) => {
			const img = new Image();
			imagesRef.current.push(img);
			const onFinish = () => {
				attempts += 1;
				// if the image successfully loaded, increment success
				if ((img as any)._loadedSuccessfully) {
					success += 1;
				}
				if (attempts === urls.length) {
					onLoaded && onLoaded({ loaded: success, attempted: attempts });
				}
			};

			img.onload = () => {
				// mark success flag (can't rely on event args across browsers)
				(img as any)._loadedSuccessfully = true;
				onFinish();
			};

			img.onerror = () => {
				(img as any)._loadedSuccessfully = false;
				onFinish();
			};

			// Start loading. If the url is relative (starts with /), the browser will resolve it.
			img.src = url;
		});

		// cleanup: dereference images to allow GC; does not abort network request.
		return () => {
			imagesRef.current.forEach((i) => {
				i.onload = null;
				i.onerror = null;
			});
			imagesRef.current = [];
		};
		// Intentionally only re-run when questionBank reference changes.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [questionBank]);

	// No visible UI. Render a tiny aria-hidden element for accessibility if needed.
	return <div aria-hidden="true" style={{ display: "none" }} />;
}
